/**
 * Hook that triggers a callback when the browser window loses focus
 * specifically due to an iframe receiving focus.
 *
 * This is useful in applications where interacting with embedded
 * content (e.g., ThoughtSpot iframe) should close or hide the current UI,
 * such as popovers, menus, or dialogs.
 *
 * @param {boolean} isVisible
 * A flag indicating whether the component (popover/menu/etc.)
 * is currently visible. The hook only listens for window blur events
 * when this value is true.
 *
 * @param {() => void} callback
 * The function to execute when an iframe becomes the active element,
 * meaning the user clicked inside or refocused an iframe.
 *
 * @example
 * // Close a popover when a ThoughtSpot iframe receives focus.
 * useIframeBlur(isPopoverOpen, () => togglePopover());
 *
 * @remarks
 * - This hook intentionally does **not** detect general window blur,
 *   only blur events where the active element is an `<iframe>`.
 * - Works well together with `useOnClickOutside` from `usehooks-ts`
 *   when replacing a more complex custom click-outside hook.
 */

import { useEffect } from 'react';

export function useIframeBlur(isVisible: boolean, callback: () => void) {
  useEffect(() => {
    if (!isVisible) return;

    const handleBlur = () => {
      const active = document.activeElement;
      if (active && active.tagName === 'IFRAME') {
        callback();
      }
    };

    window.addEventListener('blur', handleBlur);
    return () => window.removeEventListener('blur', handleBlur);
  }, [isVisible, callback]);
}
